import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

const CANVAS_SIZE = 350;

function drawFadedOutline(ctx, char, darkMode = false) {
  ctx.save();
  ctx.font = "180px 'Noto Sans Batak'";
  ctx.fillStyle = darkMode ? "#334155" : "#e5e7eb"; // slate-800 for dark, gray-200 for light
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = 0.6;
  ctx.fillText(char, ctx.canvas.width / 2, ctx.canvas.height / 2);
  ctx.restore();
}

function drawUserStroke(ctx, points, darkMode = false) {
  if (points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = darkMode ? "#f1f5f9" : "#353535"; // gray-100 for dark, gray-800 for light
  ctx.lineWidth = 14;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    ctx.quadraticCurveTo(x1, y1, (x1 + x2) / 2, (y1 + y2) / 2);
  }
  ctx.lineTo(points[points.length - 1][0], points[points.length - 1][1]);
  ctx.stroke();
  ctx.restore();
}

function drawCenterDashedCross(ctx, darkMode = false) {
  ctx.save();
  ctx.strokeStyle = darkMode ? "#334155" : "#EDEDED"; // slate-800 for dark, gray-100 for light
  ctx.lineWidth = 3;
  ctx.globalAlpha = 0.8;
  ctx.setLineDash([10, 8]);
  ctx.beginPath();
  ctx.moveTo(ctx.canvas.width / 2, 20);
  ctx.lineTo(ctx.canvas.width / 2, ctx.canvas.height - 20);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(20, ctx.canvas.height / 2);
  ctx.lineTo(ctx.canvas.width - 20, ctx.canvas.height / 2);
  ctx.stroke();
  ctx.restore();
}

function getSimilarity(canvas, char, userStroke) {
  // Draw guide to temp canvas
  const tempGuide = document.createElement("canvas");
  tempGuide.width = canvas.width;
  tempGuide.height = canvas.height;
  const ctxGuide = tempGuide.getContext("2d");
  if (!ctxGuide) return 0;
  ctxGuide.clearRect(0, 0, tempGuide.width, tempGuide.height);
  drawFadedOutline(ctxGuide, char);

  // Draw user stroke to temp canvas
  const tempUser = document.createElement("canvas");
  tempUser.width = canvas.width;
  tempUser.height = canvas.height;
  const ctxUser = tempUser.getContext("2d");
  if (!ctxUser) return 0;
  ctxUser.clearRect(0, 0, tempUser.width, tempUser.height);
  drawUserStroke(ctxUser, userStroke);

  // Get image data for both canvases
  const guideData = ctxGuide.getImageData(
    0,
    0,
    tempGuide.width,
    tempGuide.height
  ).data;
  const userData = ctxUser.getImageData(
    0,
    0,
    tempUser.width,
    tempUser.height
  ).data;

  // Compare pixels: count where guide has non-white and user also has non-white
  let match = 0,
    total = 0;
  for (let i = 0; i < guideData.length; i += 4) {
    const guideAlpha = guideData[i + 3];
    const userAlpha = userData[i + 3];
    if (guideAlpha > 10) {
      total++;
      if (userAlpha > 10) match++;
    }
  }
  // Also check how much user stroke is outside the guide
  let userTotal = 0,
    userOutside = 0;
  for (let i = 0; i < userData.length; i += 4) {
    const guideAlpha = guideData[i + 3];
    const userAlpha = userData[i + 3];
    if (userAlpha > 10) {
      userTotal++;
      if (guideAlpha < 10) userOutside++;
    }
  }
  // Calculate overlap ratio and penalty for drawing outside
  const overlap = total === 0 ? 0 : match / total;
  const outsideRatio = userTotal === 0 ? 0 : userOutside / userTotal;
  // Final score: overlap minus penalty for drawing outside
  return overlap - outsideRatio * 0.5;
}

// Define props type
interface AksaraDrawCanvasProps {
  char: string;
  checked: boolean;
  onResult?: (result: { similarity: number; isCorrect: boolean }) => void;
  onStrokeChange?: (hasStroke: boolean) => void; // add onStrokeChange
  darkMode?: boolean;
}

// Forward ref so parent can call check/reset
const AksaraDrawCanvas = forwardRef(function AksaraDrawCanvas(
  {
    char,
    checked,
    onResult,
    onStrokeChange,
    darkMode = false,
  }: AksaraDrawCanvasProps,
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [userStrokes, setUserStrokes] = useState<number[][][]>([]);
  const [currentStroke, setCurrentStroke] = useState<number[][]>([]);
  const [isDrawing, setIsDrawing] = useState(false);

  // Drawing logic
  const handlePointerDown = (e: React.PointerEvent) => {
    if (checked) return;
    setIsDrawing(true);
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCurrentStroke([[x, y]]);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing || checked) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCurrentStroke((prev) => [...prev, [x, y]]);
  };

  const handlePointerUp = () => {
    if (checked) return;
    setIsDrawing(false);
    if (currentStroke.length > 1) {
      setUserStrokes((prev) => [...prev, currentStroke]);
    }
    setCurrentStroke([]);
  };

  // Redraw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = (canvas as HTMLCanvasElement).getContext("2d");
    ctx!.clearRect(0, 0, canvas.width, canvas.height);
    drawCenterDashedCross(ctx!, darkMode);
    drawFadedOutline(ctx!, char, darkMode);
    userStrokes.forEach((stroke) => drawUserStroke(ctx!, stroke, darkMode));
    if (currentStroke.length > 0) drawUserStroke(ctx!, currentStroke, darkMode);
  }, [userStrokes, currentStroke, char]);

  // Notify parent when stroke changes
  useEffect(() => {
    if (onStrokeChange) {
      onStrokeChange(userStrokes.length > 0 || currentStroke.length > 0);
    }
  }, [userStrokes, currentStroke, onStrokeChange]);

  // Expose check/reset to parent
  useImperativeHandle(ref, () => ({
    check: () => {
      const canvas = canvasRef.current;
      if (!canvas) return { similarity: 0, isCorrect: false };
      const allPoints = userStrokes.flat();
      const similarity = getSimilarity(canvas, char, allPoints);
      const isCorrect = similarity > 0.6;
      if (onResult) onResult({ similarity, isCorrect });
      return { similarity, isCorrect };
    },
    reset: () => {
      setUserStrokes([]);
      setCurrentStroke([]);
    },
    hasStroke: () => userStrokes.length > 0,
  }));

  // Reset strokes if checked changes from true to false
  useEffect(() => {
    if (!checked) {
      setUserStrokes([]);
      setCurrentStroke([]);
    }
  }, [checked]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_SIZE}
      height={CANVAS_SIZE}
      className={`rounded-2xl shadow-lg touch-none ${
        darkMode ? "bg-[#1e293b]" : "bg-white"
      }`}
      style={{ touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    />
  );
});

export default AksaraDrawCanvas;
